import {
  computed, watch, reactive, ref,
} from 'vue';
import { createSharedComposable } from '@vueuse/core';
import { useColor } from '@common-vue/media-card-large/src/modules/color';
import { useUpc } from '@common-vue/media-card-large';
import { usePageData } from '@composables/usePageData';
import useHexa from '@composables/useHexa';
import { useModal } from '@composables/utils/useModal';
import { useKillswitches } from '@modules/featureControl';
import useMarxentCta from '@composables/useProductImagesCta/useMarxentCta';
import getMatchingColors from '../../utils/getMatchingColors';
import { tagHexa360 } from '../../features/analytics/hexa';
import { useProductImagesCtaActions } from './useProductImagesCtaActions';

export const useWebAr = createSharedComposable((sortedList = []) => {
  const { addCtaItem, removeCtaItem, updateCtaItem } = useProductImagesCtaActions();
  const { open } = useModal();
  const { productId } = usePageData();
  const { selectedUpcNumber } = useUpc(productId);
  const { fetch, result: hexaColors } = useHexa(productId);
  const { killswitches } = useKillswitches();
  const { colors: productColors } = useColor(productId);
  const { isArEligible, marxentCtaConfig, setCtaConfig } = useMarxentCta();
  const hexaEligible = ref(false);
  const isHexaOpen = ref(false);

  /**
   * Function that changes isHexaOpen to passed in value
  * @param {boolean} val
  */

  const setHexaState = (val) => {
    isHexaOpen.value = val;
  };

  const hexaCtaConfig = reactive({
    customCssClass: 'font-big',
    id: 'hexa',
    label: 'SEE IT<br/>IN 3D',
    onClickHandler: () => {
      setHexaState(true);
      open('hexa');
      tagHexa360();
    },
  });

  /**
   * Function that checks to see if the opposite CTA has already been added to sorted list and then removes it
   * before adding current CTA
   * if marxent has been added already, it will call updateCtaItem instead of addCtaItem
   *
 * @param {object} currentCtaConfig
 * @param {string} oppositeCtaName
 */

  const addCtaAndRemoveOpposite = (currentCtaConfig = {}, oppositeCtaName = '') => {
    if (sortedList.value.some((obj) => obj.id === oppositeCtaName)) {
      removeCtaItem(oppositeCtaName);
    }
    if (oppositeCtaName !== 'marxent' && sortedList.value.some((obj) => obj.id === 'marxent')) {
      updateCtaItem('marxent', currentCtaConfig);
    } else {
      addCtaItem(currentCtaConfig);
    }
  };

  /**
    Function that fetches from hexa api, checks for hexa eligibility and triggers addCtaItem
  */

  const checkHexa = async () => {
    await fetch();
    // TODO: Cache hexa call so we do not fetch every upc change
    const hasMatchingHexaColors = computed(() => hexaColors.value && getMatchingColors(productColors.value, hexaColors.value));
    if (hasMatchingHexaColors.value) {
      addCtaAndRemoveOpposite(hexaCtaConfig, 'marxent');
      hexaEligible.value = true;
    }
  };

  /**
    Function to separate concerns between hexa and marxent, triggers addCtaAndRemoveOpposite
    and
  */
  const checkMarxent = (config) => {
    addCtaAndRemoveOpposite(config, 'hexa');
  };

  /**
    Function responsible for checking marxent & hexa KS, setting config for adding/updating CTA
    check if marxent isEligible, trigger hexa if the modal is not open
  */

  const webArCheck = async () => {
    // If web ar kill switch is on, then we check if selected upc is ar eligible
    if (killswitches.isBigTicketWebAREnabled) {
      await setCtaConfig();
      if (isArEligible.value) {
        checkMarxent(marxentCtaConfig.value);
        return;
      }
    }
    if (killswitches.hexaEnabled && !isHexaOpen?.value) {
      checkHexa();
    }
  };

  watch([selectedUpcNumber, isHexaOpen, isArEligible], () => {
    if (!isHexaOpen.value) webArCheck();
  },
  { immediate: true });
  return { isHexaOpen, setHexaState };
});

export default useWebAr;
